import React from 'react';
import { TrendingUp, Share2, Target, Code, Palette, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { services } from '../mockData';

const iconMap = {
  TrendingUp,
  Share2,
  Target,
  Code,
  Palette,
  Users
};

const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Digital Marketing Services - Arikone | Social Media, Performance Ads & Website Development"
        description="Comprehensive digital marketing services including social media marketing, performance ads, website development, branding & creative design, and lead generation. Expert digital marketing solutions in Noida & Delhi NCR."
        keywords="digital marketing services, social media marketing, performance ads, website development, branding services, creative design, lead generation, SEO services noida"
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-20 lg:py-32 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-1/2 -right-1/2 w-[1000px] h-[1000px] rounded-full bg-blue-500/10 blur-3xl animate-pulse" />
          <div className="absolute -bottom-1/2 -left-1/2 w-[800px] h-[800px] rounded-full bg-purple-500/10 blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <div className="text-left animate-fadeInUp">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 mb-6 backdrop-blur-sm">
                <span className="flex h-2 w-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
                Digital Excellence
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Transform Your <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Digital Presence
                </span>
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 max-w-xl leading-relaxed">
                Elevate your brand with our comprehensive suite of digital services. 
                From strategic marketing to cutting-edge development, we build solutions that drive growth.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-blue-600/25 transition-all hover:scale-105"
                >
                  <Link to="/contact">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-blue-400/30 text-blue-100 hover:bg-blue-900/50 hover:text-white px-8 py-6 text-lg rounded-full backdrop-blur-sm transition-all"
                >
                  View Case Studies
                </Button>
              </div>
            </div>

            {/* Right Visuals */}
            <div className="relative hidden lg:block h-[500px]">
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 to-purple-600/20 rounded-full blur-3xl" />
              
              {/* Floating Icons Grid/Cloud */}
              <div className="relative w-full h-full">
                {/* Center Piece */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-2xl shadow-2xl animate-float">
                  <div className="text-center">
                    <span className="block text-5xl mb-2">🚀</span>
                    <div className="text-2xl font-bold text-white">10x Growth</div>
                    <div className="text-sm text-blue-200">Guaranteed Results</div>
                  </div>
                </div>

                {/* Orbiting Elements */}
                <div className="absolute top-10 right-10 bg-blue-500/20 backdrop-blur-md p-4 rounded-xl border border-blue-400/30 animate-bounce delay-100 shadow-lg shadow-blue-500/20">
                  <TrendingUp className="text-blue-300 w-8 h-8" />
                </div>
                <div className="absolute bottom-20 left-10 bg-purple-500/20 backdrop-blur-md p-4 rounded-xl border border-purple-400/30 animate-bounce delay-300 shadow-lg shadow-purple-500/20">
                  <Target className="text-purple-300 w-8 h-8" />
                </div>
                <div className="absolute top-20 left-0 bg-indigo-500/20 backdrop-blur-md p-4 rounded-xl border border-indigo-400/30 animate-bounce delay-500 shadow-lg shadow-indigo-500/20">
                  <Share2 className="text-indigo-300 w-8 h-8" />
                </div>
                <div className="absolute bottom-0 right-20 bg-cyan-500/20 backdrop-blur-md p-4 rounded-xl border border-cyan-400/30 animate-bounce delay-700 shadow-lg shadow-cyan-500/20">
                  <Code className="text-cyan-300 w-8 h-8" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              
              return (
                <Card
                  key={service.id}
                  className="hover-glow border-2 animate-fadeInUp"
                >
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="text-blue-600" size={32} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-3">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 mb-6">{service.description}</p>
                        <div className="space-y-3">
                          <p className="font-semibold text-gray-900">Key Features:</p>
                          <ul className="space-y-2">
                            {service.features.map((feature, idx) => (
                              <li key={idx} className="flex items-center text-gray-700">
                                <CheckCircle className="text-green-500 mr-3 flex-shrink-0" size={18} />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A systematic approach to delivering exceptional results
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Understanding your business, goals, and target audience' },
              { step: '02', title: 'Strategy', description: 'Creating a customized plan tailored to your objectives' },
              { step: '03', title: 'Execution', description: 'Implementing campaigns with precision and creativity' },
              { step: '04', title: 'Optimization', description: 'Continuous monitoring and improvement for maximum ROI' }
            ].map((processItem, index) => {
              return (
                <div
                  key={index}
                  className="text-center animate-fadeInUp"
                >
                  <div className="text-5xl font-bold text-blue-600 mb-4">{processItem.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{processItem.title}</h3>
                  <p className="text-gray-600">{processItem.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Let's Grow Your Business Together
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Schedule a free consultation to discuss your marketing needs
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg btn-primary"
          >
            <Link to="/contact">
              Get Started Today <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
